import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import MultiSelectField from '../../components/Fields/MultiSelectField';
import SwitchField from '../../components/Fields/SwitchField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import AttachmentModel from '../../models/AttachmentModel';
import DataService from '../../services/data.service';
import Button from './../../components/Buttons/Button';
import ButtonBar from './../../components/Fields/ButtonBar';
import Form from './../../components/Fields/Form';
import SelectListField from './../../components/Fields/SelectListField';
import TextboxField from './../../components/Fields/TextboxField';
import Unauthorized from './../Authentication/Unauthorized';
import AttachmentCard from './Components/AttachmentCard';

interface AttachmentSearchCriteria {
    quickSearch: string
    itemNr: string
    attachmentTypeIds: number[]
    itemTypeIds: number[]
    brandId?: number
    productCategoryId?: number
    employeeIds: number[]
    userIds: number[]
    projectIds: number[]
    supplier_BusinessPartnerId?: number
    customer_BusinessPartnerId?: number
    licensor_BusinessPartnerId?: number
    itemStatusTypeId?: number
    itemStatusIds: number[]
    designPhases: number[]
    includeUnsearchableItems: boolean
    searchInBomItems: boolean
    activeOnly: boolean
    rememberFilter: boolean
}

const AttachmentSearchView: React.FC = () => {
    const [data, setData] = useState<AttachmentModel[]>([])
    const [downloadUrl, setDownloadUrl] = useState<string>()
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const { handleException } = useNotifications();
    const navigate = useNavigate()

    const [formData, setFormData] = useState<AttachmentSearchCriteria>({
        quickSearch: '',
        itemNr: '',
        attachmentTypeIds: [],
        itemTypeIds: [],
        brandId: 0,
        productCategoryId: 0,
        employeeIds: [],
        userIds:[],
        projectIds: [],
        itemStatusIds: [],
        designPhases: [],
        includeUnsearchableItems: true,
        searchInBomItems: false,
        activeOnly: true,
        rememberFilter: false,
    });

    const attachmentTypes = useSelectListOptions('api/v1/selectlist/AttachmentTypes?active=' + formData.activeOnly + '&applicationFunction=' + ApplicationFunctions.SearchAttachments);
    const users = useSelectListOptions('api/v1/selectlist/Users?active=' + formData.activeOnly + '&applicationFunction=' + ApplicationFunctions.SearchAttachments);
    const itemTypes = useSelectListOptions(`api/v1/selectlist/ItemTypes?activeOnly=${formData.activeOnly}&applicationFunctionId=${ApplicationFunctions.SearchAttachments}`);
    const brands = useSelectListOptions(`api/v1/selectlist/BrandNameTrees?activeOnly=${formData.activeOnly}`, '-- All brands');
    const productCategories = useSelectListOptions('api/v1/selectlist/ProductCategoryNameTrees?activeOnly=' + formData.activeOnly , '-- All productcategories');
    const customers = useSelectListOptions('api/v1/selectlist/businessPartners?activeOnly=' + formData.activeOnly + '&businessPartnerTypeId=1', '-- All customers')
    const suppliers = useSelectListOptions('api/v1/selectlist/businessPartners?activeOnly=' + formData.activeOnly + '&businessPartnerTypeId=2', '-- All suppliers')
    const licensors = useSelectListOptions('api/v1/selectlist/businessPartners?activeOnly=' + formData.activeOnly + '&businessPartnerTypeId=3', '-- All licensors')
    const projects = useSelectListOptions('api/v1/selectlist/projects?activeOnly=' + formData.activeOnly)
    const employees = useSelectListOptions('api/v1/selectlist/employees?activeOnly=' + formData.activeOnly)
    const statuses = useSelectListOptions('api/v1/selectlist/itemStatuses?activeOnly=' + formData.activeOnly)
    const designPhases = useSelectListOptions('api/v1/selectlist/designphases?activeOnly=' + formData.activeOnly)

    useEffect(() => {
        const filterItem = localStorage.getItem("attachmentFilter");
        if (filterItem !== null) {
            let filter: AttachmentSearchCriteria = JSON.parse(filterItem);
            setFormData(filter);
            searchWithCriteria(filter);
        }
    }, [])

    function search() {
        searchWithCriteria(formData)
    }

    function searchWithCriteria(searchCriteria) {
        setShowSpinner(true);

        if (searchCriteria.rememberFilter)
            localStorage.setItem('attachmentFilter', JSON.stringify(searchCriteria));
        else
            localStorage.removeItem(`attachmentFilter`);

        DataService
            .post<AttachmentModel[]>(`api/v1/Attachments/Search/`, searchCriteria)
            .then(response => {
                setShowSpinner(false);
                setData(response);
            })
            .catch(e => {
                setShowSpinner(false);
                handleException(e)
            });
    }

    function setDefaultCriteria() {
        let criteria = {
            quickSearch: '',
            itemNr: '',
            attachmentTypeIds: [],
            itemTypeIds: [],
            brandId: 0,
            productCategoryId: 0,
            employeeIds: [],
            userIds: [],
            projectIds: [],
            itemStatusIds: [],
            designPhases: [],
            includeUnsearchableItems: true,
            searchInBomItems: false,
            activeOnly: true,
            rememberFilter: formData?.rememberFilter ?? false,
        }

        setFormData({...criteria})
    }

    function onFieldChanged(name: string, value: string): void {
        formData[name] = value;
        setFormData(formData);
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'quickSearch', label: 'Search', labelSize: 3 },
            { name: 'attachmentTypeIds', label: 'Attachment type', labelSize: 3 },
            { name: 'itemNr', label: 'Item Nr', labelSize: 3 },
            { name: 'itemTypeIds', label: 'Item types', labelSize: 3 },
            { name: 'brandId', label: 'Brand', labelSize: 3 },
            { name: 'productCategoryId', label: 'Product category', labelSize: 3 },
            { name: 'supplier_BusinessPartnerId', label: 'Supplier', labelSize: 3 },
            { name: 'customer_BusinessPartnerId', label: 'Customer', labelSize: 3 },
            { name: 'licensor_BusinessPartnerId', label: 'Licensor', labelSize: 3 },
            { name: 'projectIds', label: 'Project', labelSize: 3 },
            { name: 'userIds', label: 'User', labelSize: 3 },
            { name: 'employeeIds', label: 'Employee', labelSize: 3 },
            { name: 'itemStatusIds', label: 'Item Status', labelSize: 3 },
            { name: 'designPhases', label: 'Item design phase', labelSize: 3 },
            { name: 'searchInBomItems', label: 'Search in bill-of-materials', labelSize: 3 },
            { name: 'activeOnly', label: 'Search for active items only', labelSize: 3 },
            { name: 'rememberFilter', label: 'Remember search criteria', labelSize: 3 },

        ],
        handleSubmit: search,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    const { isAuthorizedFor } = useAuth();
    if (!isAuthorizedFor(ApplicationFunctions.SearchAttachments)) {
        return (<Unauthorized />);
    }

    let results = data.map((item, index) => {
        return <AttachmentCard attachment={item} handleItemClicked={itemClicked} handleItemPreviewClicked={itemPreviewClicked} key={index} />
    });

    function itemClicked(attachment: AttachmentModel) {
        let url = '/plm/common/attachment/' + attachment.id;
        let navigateTitle = 'Attachment';
        navigate(url, { state: { title: navigateTitle, fromMenu: false } });
    }

    function itemPreviewClicked(attachment: AttachmentModel) {
        var fileUrl = attachment.attachmentVersions[attachment.latestVersion - 1].fileUrl
        setDownloadUrl(fileUrl)
    }


    return (
        <div className='main-content'>
            <Form title='Attachments' footer={(<>
                <ButtonBar offset={1}>
                    <Button text='Search' {...getSubmitButtonProps()} />
                    <Button text='Clear' type='button' onClick={setDefaultCriteria} />
                </ButtonBar>
            </>)}>
                <Container fluid>
                    <Row>
                        <Col md={6}>
                            <TextboxField {...getFieldProps('quickSearch')} />
                            <MultiSelectField {...getFieldProps('attachmentTypeIds')} options={attachmentTypes} />
                            <TextboxField {...getFieldProps('itemNr')} />
                            <MultiSelectField {...getFieldProps('itemTypeIds')} options={itemTypes} />
                            <SelectListField {...getFieldProps('brandId')} options={brands} />
                            <SelectListField {...getFieldProps('productCategoryId')} options={productCategories} />
                            <SelectListField {...getFieldProps('supplier_BusinessPartnerId')} options={suppliers} />
                            <SelectListField {...getFieldProps('customer_BusinessPartnerId')} options={customers} />
                            <SelectListField {...getFieldProps('licensor_BusinessPartnerId')} options={licensors} />
                        </Col>
                        <Col md={6}>
                            <MultiSelectField {...getFieldProps('projectIds')} options={projects} />
                            <MultiSelectField {...getFieldProps('userIds')} options={users} />
                            <MultiSelectField {...getFieldProps('employeeIds')} options={employees} />
                            <MultiSelectField {...getFieldProps('itemStatusIds')} options={statuses} />
                            <MultiSelectField {...getFieldProps('designPhases')} options={designPhases} />
                            <SwitchField {...getFieldProps('searchInBomItems')} />
                            <SwitchField {...getFieldProps('activeOnly')} />
                            <SwitchField {...getFieldProps('rememberFilter')} />
                        </Col>
                    </Row>
                </Container>
            </Form>
            <Container fluid>
                <Row>
                    <Col md={8}>
                        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-around', maxHeight: 950, overflow: 'auto' }}>
                            {results}
                        </div>
                    </Col>
                    <Col md={4}>
                        {downloadUrl && <iframe src={downloadUrl} style={{marginTop:30, width:'100%', height:'100%'}}></iframe>}
                    </Col>
                </Row>
            </Container>
            {showSpinner && (<SpinnerOverlay />)}
        </div>
    );
}
export default AttachmentSearchView;